"use client";

import { useRef, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { X, Star, CheckCircle2 } from "lucide-react";
import { Product } from "@/actions/products";
import { ProductActions } from "@/components/shop/ProductActions";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

interface QuickViewModalProps {
    product: Product;
    onClose: () => void;
    isOpen: boolean;
}

export function QuickViewModal({ product, onClose, isOpen }: QuickViewModalProps) {
    const modalRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    useGSAP(() => {
        if (isOpen) {
            gsap.to(overlayRef.current, { opacity: 1, duration: 0.3, pointerEvents: "all" });
            gsap.fromTo(contentRef.current,
                { y: 50, opacity: 0, scale: 0.95 },
                { y: 0, opacity: 1, scale: 1, duration: 0.4, ease: "power3.out", delay: 0.1 }
            );
        } else {
            gsap.to(overlayRef.current, { opacity: 0, duration: 0.3, pointerEvents: "none" });
            gsap.to(contentRef.current, { y: 50, opacity: 0, scale: 0.95, duration: 0.3, ease: "power3.in" });
        }
    }, [isOpen]);

    // Close on escape key
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handleEsc);
        return () => window.removeEventListener("keydown", handleEsc);
    }, [onClose]);

    if (!product) return null;

    const discount = product.price && product.salePrice
        ? Math.round(((product.price - product.salePrice) / product.price) * 100)
        : 0;

    return (
        <div ref={modalRef} className={`fixed inset-0 z-[100] flex items-center justify-center p-4 ${isOpen ? "pointer-events-auto" : "pointer-events-none"}`}>
            {/* Overlay */}
            <div
                ref={overlayRef}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm opacity-0 pointer-events-none"
                onClick={onClose}
            ></div>

            {/* Modal Content */}
            <div
                ref={contentRef}
                className="bg-white w-full max-w-4xl rounded-3xl shadow-2xl overflow-hidden relative opacity-0"
            >
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/80 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-red-500 transition-all"
                >
                    <X className="w-5 h-5" />
                </button>

                <div className="flex flex-col lg:flex-row h-full max-h-[85vh] lg:h-auto overflow-y-auto lg:overflow-visible">
                    {/* Image Section */}
                    <div className="w-full lg:w-1/2 bg-gray-50 p-8 flex items-center justify-center min-h-[300px]">
                        <div className="relative w-full aspect-square max-w-sm">
                            <Image
                                src={product.images[0]}
                                alt={product.name}
                                fill
                                className="object-contain"
                            />
                        </div>
                    </div>

                    {/* Info Section */}
                    <div className="w-full lg:w-1/2 p-8 lg:p-10 flex flex-col">
                        <div className="mb-1">
                            <span className="text-blue-600 font-bold uppercase tracking-wider text-xs">{product.brand}</span>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-3 leading-tight">
                            {product.name}
                        </h2>

                        {/* Rating */}
                        <div className="flex items-center gap-4 mb-6">
                            <div className="flex text-yellow-400">
                                {[...Array(5)].map((_, i) => (
                                    <Star key={i} className={`w-4 h-4 ${i < Math.floor(product.rating) ? "fill-current" : "text-gray-200"}`} />
                                ))}
                            </div>
                            <span className="text-sm text-gray-500">({product.reviews} reviews)</span>
                        </div>

                        {/* Price */}
                        <div className="flex items-center gap-3 mb-6">
                            {product.price ? (
                                product.salePrice ? (
                                    <>
                                        <span className="text-3xl font-bold text-blue-600">LKR {product.salePrice.toLocaleString()}</span>
                                        <span className="text-lg text-gray-400 line-through">LKR {product.price.toLocaleString()}</span>
                                        <span className="bg-red-100 text-red-600 px-2 py-1 rounded text-xs font-bold">-{discount}%</span>
                                    </>
                                ) : (
                                    <span className="text-3xl font-bold text-gray-900">LKR {product.price.toLocaleString()}</span>
                                )
                            ) : (
                                <span className="text-3xl font-bold text-blue-600">Call for Price</span>
                            )}
                        </div>

                        <p className="text-gray-600 leading-relaxed mb-8 line-clamp-3">
                            {product.shortDescription}
                        </p>

                        <div className="space-y-2 mb-8">
                            {product.stock > 0 ? (
                                <div className="flex items-center gap-2 text-green-600 text-sm font-medium">
                                    <CheckCircle2 className="w-4 h-4" /> In Stock
                                </div>
                            ) : (
                                <div className="text-red-500 text-sm font-medium">Out of Stock</div>
                            )}
                            <div className="text-sm text-gray-500">
                                SKU: <span className="text-gray-900 font-mono">{product.id}</span>
                            </div>
                        </div>

                        <div className="mt-auto">
                            <ProductActions
                                productName={product.name}
                                productId={product.id}
                                price={product.salePrice || product.price}
                            />

                            <div className="mt-4 text-center">
                                <Link href={`/products/${product.slug}`} className="text-blue-600 font-medium hover:underline text-sm" onClick={onClose}>
                                    View Full Details
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
